import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, HostListener, ElementRef, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ConfigService } from 'src/app/services/config.service';
import { IdentityService } from 'src/app/services/identity.service';
import { NotificationService } from 'src/app/services/notification.service';
import { WidgetContextService } from 'src/app/services/widget-context.service';
import { DialogService } from '../../../services/dialog.service';

// Image extensions for preview
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico']);

// PDF extension
const PDF_EXTENSIONS = new Set(['pdf']);

// File extensions that Monaco can edit
const TEXT_EXTENSIONS = new Set([
  // Code
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs',
  'py', 'pyw', 'pyi',
  'java', 'kt', 'kts', 'scala',
  'c', 'h', 'cpp', 'hpp', 'cc', 'cxx',
  'cs', 'fs', 'fsx',
  'go', 'rs', 'rb', 'php',
  'swift', 'dart', 'lua', 'pl', 'pm',
  'r', 'R', 'jl',
  // Web
  'html', 'htm', 'css', 'scss', 'sass', 'less',
  'vue', 'svelte', 'astro',
  // Config/Data
  'json', 'jsonc', 'yaml', 'yml', 'toml',
  'xml', 'xsl', 'xslt', 'svg',
  'ini', 'cfg', 'conf', 'config',
  'env', 'properties',
  'csv', 'tsv',
  // Shell/Script
  'sh', 'bash', 'zsh', 'fish',
  'ps1', 'psm1', 'bat', 'cmd',
  // Docs
  'md', 'markdown', 'txt', 'rst', 'adoc',
  'tex', 'bib',
  // SQL
  'sql', 'mysql', 'pgsql',
  // Other
  'dockerfile', 'makefile', 'cmake',
  'gitignore', 'gitattributes', 'editorconfig',
  'lock', 'log',
]);

const MAX_FILE_SIZE = 1024 * 1024; // 1MB

interface FileItem {
  name: string;
  type: 'file' | 'directory' | 'unknown';
  size: number;
  modified: Date | null;
}

interface Shortcut {
  name: string;
  path: string;
  icon: string;
}

@Component({
    selector: 'app-files-widget',
    templateUrl: './files-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './files-widget.component.scss'],
    standalone: false
})
export class FilesWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  @Input() initialPath: string = null;  // Path to navigate to on init
  @Input() selectFile: string = null;   // Filename to select after navigation
  @Output() onEditFile = new EventEmitter<{ parentId: string; path: string }>();
  @Output() onPreviewFile = new EventEmitter<{ parentId: string; path: string; previewType: 'image' | 'pdf' }>();
  @Output() onViewMarkdown = new EventEmitter<{ parentId: string; path: string; title: string }>();
  @Output() onViewCsv = new EventEmitter<{ parentId: string; path: string; title: string }>();

  // Callback alternatives for when EventEmitter outputs aren't available (e.g., ngComponentOutlet)
  @Input() editFileCallback: ((path: string) => void) | null = null;
  @Input() viewMarkdownCallback: ((path: string, title: string) => void) | null = null;
  @Input() viewCsvCallback: ((path: string, title: string) => void) | null = null;
  @Input() previewFileCallback: ((path: string, previewType: 'image' | 'pdf') => void) | null = null;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  public currentPath: string = '';
  public pathSegments: string[] = [];
  public items: FileItem[] = [];
  public loading: boolean = false;

  // Alias for snapshot capture convention
  public get isLoading(): boolean {
    return this.loading;
  }
  public error: string | null = null;
  public selectedItem: FileItem | null = null;

  // All possible shortcuts - will be filtered to only show existing ones
  private allShortcuts: Shortcut[] = [
    { name: 'Home', path: '~', icon: 'home' },
    { name: 'Desktop', path: '~/Desktop', icon: 'desktop_windows' },
    { name: 'Documents', path: '~/Documents', icon: 'description' },
    { name: 'Downloads', path: '~/Downloads', icon: 'download' },
    { name: 'Music', path: '~/Music', icon: 'library_music' },
    { name: 'Pictures', path: '~/Pictures', icon: 'photo_library' },
    { name: 'Videos', path: '~/Videos', icon: 'video_library' },
    { name: 'Data', path: '/data', icon: 'storage' },
    { name: 'Trash', path: '~/.local/share/Trash/files', icon: 'delete' },
  ];

  // Filtered to only existing directories
  public shortcuts: Shortcut[] = [];

  public showHiddenFiles: boolean = false;
  public viewMode: 'grid' | 'list' = 'grid';
  private _userViewMode: 'grid' | 'list' = 'grid'; // User's preferred mode (before narrow override)
  private _narrowOverride: boolean = false;
  private _resizeObserver: ResizeObserver | null = null;
  private homePath: string = '';

  // Drag and drop state
  public isDragging: boolean = false;
  public uploadProgress: { name: string; progress: number; status: string }[] = [];

  // Rename state
  public renamingItem: FileItem | null = null;
  public renameValue: string = '';

  // Path edit state (GNOME-style click-to-edit breadcrumb)
  public editingPath: boolean = false;
  public editPathValue: string = '';

  // Filter state
  private _filterText: string = '';
  @ViewChild('filterInput') filterInput: ElementRef<HTMLInputElement>;
  @ViewChild('breadcrumbEl') breadcrumbEl: ElementRef<HTMLDivElement>;
  @ViewChild('pathInput') pathInput: ElementRef<HTMLInputElement>;

  get filterText(): string {
    return this._filterText;
  }

  set filterText(value: string) {
    this._filterText = value;
    this.cdr.detectChanges();
  }

  constructor(
    private http: HttpClient,
    private identity: IdentityService,
    private cdr: ChangeDetectorRef,
    private notification: NotificationService,
    private elRef: ElementRef,
    private _widgetContext: WidgetContextService,
  ) {
    super();
  }

  private readonly STORAGE_KEY_PREFIX = 'files-widget-path';

  private get storageKey(): string {
    const username = this.getUsername();
    return `${this.STORAGE_KEY_PREFIX}-${username}`;
  }

  ngOnInit(): void {
    // Check which shortcuts exist and filter the list
    this.initShortcuts();

    // Use initialPath if provided, otherwise restore last path or start at home
    if (this.initialPath) {
      this.navigateTo(this.initialPath);
    } else {
      const savedPath = localStorage.getItem(this.storageKey);
      this.navigateTo(savedPath || '~');
    }

    // Auto-switch to list view when widget is narrow
    this._resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0 && width < 500) {
        if (!this._narrowOverride) {
          this._narrowOverride = true;
          this.viewMode = 'list';
        }
      } else if (this._narrowOverride) {
        this._narrowOverride = false;
        this.viewMode = this._userViewMode;
      }
    });
    this._resizeObserver.observe(this.elRef.nativeElement);
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._widgetContext.removeContext(this.id);
  }

  private initShortcuts(): void {
    // Home always exists, start with it
    this.shortcuts = [this.allShortcuts[0]];

    // Check all paths in a single API call
    const pathsToCheck = this.allShortcuts.slice(1).map(s => s.path);

    this.http.post<any>(`${ConfigService.muxUrl}/commander/check_paths`, { paths: pathsToCheck })
      .subscribe({
        next: (response) => {
          if (response.success && response.paths) {
            // Add shortcuts for paths that exist as directories
            for (const shortcut of this.allShortcuts.slice(1)) {
              const pathInfo = response.paths[shortcut.path];
              if (pathInfo?.exists && pathInfo?.type === 'directory') {
                this.shortcuts.push(shortcut);
              }
            }
          }
          this.cdr.detectChanges();
        },
        error: () => {
          // If the new endpoint doesn't exist, shortcuts just won't be added
        }
      });
  }

  navigateTo(path: string): void {
    this.loading = true;
    this.error = null;
    this.selectedItem = null;
    this.filterText = '';  // Clear filter when navigating

    this.http.post<any>(`${ConfigService.muxUrl}/commander/list_directory`, { path })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.currentPath = response.metadata.path;
            this.items = this.sortItems(response.data);
            this.updatePathSegments();

            // Store home path for ~ shortcut
            if (path === '~') {
              this.homePath = this.currentPath;
            }

            // Save path to localStorage for persistence
            localStorage.setItem(this.storageKey, this.currentPath);

            // Update widget context for Claude
            this._widgetContext.updateContext({
              widgetId: this.id,
              widgetType: 'FilesWidget',
              path: this.currentPath,
              state: {
                fileCount: this.items?.length || 0,
              },
            });

            // Select file if specified
            if (this.selectFile) {
              const fileToSelect = this.items.find(item => item.name === this.selectFile);
              if (fileToSelect) {
                this.selectedItem = fileToSelect;
                // Scroll selected item into view after DOM updates
                setTimeout(() => {
                  const selectedEl = document.querySelector('.file-item.selected, .file-row.selected');
                  if (selectedEl) {
                    selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }
              this.selectFile = null;  // Clear after use
            }
          } else {
            this.error = response.error || 'Failed to load directory';
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.error = err.error?.message || 'Failed to load directory';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private sortItems(items: FileItem[]): FileItem[] {
    // Directories first, then files, alphabetically within each group
    return items.sort((a, b) => {
      if (a.type === 'directory' && b.type !== 'directory') return -1;
      if (a.type !== 'directory' && b.type === 'directory') return 1;
      return a.name.localeCompare(b.name);
    });
  }

  get visibleItems(): FileItem[] {
    let items = this.items;

    // Filter hidden files
    if (!this.showHiddenFiles) {
      items = items.filter(item => !item.name.startsWith('.'));
    }

    // Filter by search text
    if (this.filterText) {
      const search = this.filterText.toLowerCase();
      items = items.filter(item => item.name.toLowerCase().includes(search));
    }

    return items;
  }

  clearFilter(): void {
    this.filterText = '';
  }

  onFilterKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.filterText) {
        this.clearFilter();
      } else {
        this.filterInput?.nativeElement.blur();
      }
    }
  }

  toggleHiddenFiles(): void {
    this.showHiddenFiles = !this.showHiddenFiles;
  }

  toggleViewMode(): void {
    this._userViewMode = this._userViewMode === 'grid' ? 'list' : 'grid';
    // Only apply if not overridden by narrow width
    if (!this._narrowOverride) {
      this.viewMode = this._userViewMode;
    }
  }

  private updatePathSegments(): void {
    // Split path into clickable segments
    const parts = this.currentPath.split('/').filter(p => p);
    this.pathSegments = parts;

    // Scroll breadcrumb to the end so the current directory is visible
    setTimeout(() => {
      const el = this.breadcrumbEl?.nativeElement;
      if (el) el.scrollLeft = el.scrollWidth;
    });
  }

  private getUsername(): string {
    return this.identity.me?.username || 'user';
  }

  onItemClick(item: FileItem): void {
    this.selectedItem = item;
  }

  onItemDoubleClick(item: FileItem): void {
    if (item.type === 'directory') {
      this.navigateTo(`${this.currentPath}/${item.name}`);
    } else {
      this.openFile(item);
    }
  }

  private openFile(item: FileItem): void {
    const ext = item.name.split('.').pop()?.toLowerCase() || '';
    const filePath = `${this.currentPath}/${item.name}`;

    // Check if it's an image - open in preview widget
    if (IMAGE_EXTENSIONS.has(ext)) {
      if (this.previewFileCallback) {
        this.previewFileCallback(filePath, 'image');
      } else {
        this.onPreviewFile.emit({ parentId: this.id, path: filePath, previewType: 'image' });
      }
      return;
    }

    // Check if it's a PDF - open in preview widget
    if (PDF_EXTENSIONS.has(ext)) {
      if (this.previewFileCallback) {
        this.previewFileCallback(filePath, 'pdf');
      } else {
        this.onPreviewFile.emit({ parentId: this.id, path: filePath, previewType: 'pdf' });
      }
      return;
    }

    // Check if it's markdown - open in markdown viewer
    if (ext === 'md' || ext === 'markdown') {
      if (this.viewMarkdownCallback) {
        this.viewMarkdownCallback(filePath, item.name);
      } else {
        this.onViewMarkdown.emit({ parentId: this.id, path: filePath, title: item.name });
      }
      return;
    }

    // Check if it's a CSV - open in CSV viewer (spreadsheet)
    if (ext === 'csv') {
      if (this.viewCsvCallback) {
        this.viewCsvCallback(filePath, item.name);
      } else {
        this.onViewCsv.emit({ parentId: this.id, path: filePath, title: item.name });
      }
      return;
    }

    // Check if file can be opened in Monaco
    if (!this.isTextFile(item.name)) {
      console.warn('Cannot open file type:', item.name);
      this.notification.showError('Unrecognized file type');
      return;
    }

    // Check file size
    if (item.size > MAX_FILE_SIZE) {
      console.warn('File too large to open:', item.name, item.size);
      this.notification.showError(`File too large to open (${this.formatSize(item.size)})`);
      return;
    }

    if (this.editFileCallback) {
      this.editFileCallback(filePath);
    } else {
      this.onEditFile.emit({ parentId: this.id, path: filePath });
    }
  }

  onBackgroundClick(event: MouseEvent): void {
    // Only deselect if clicking on the background, not on a file item
    const target = event.target as HTMLElement;
    if (target.classList.contains('file-list')) {
      this.selectedItem = null;
    }
  }

  private isTextFile(filename: string): boolean {
    // Check for extensionless files that are commonly text
    const lowerName = filename.toLowerCase();
    if (['dockerfile', 'makefile', 'readme', 'license', 'changelog', 'authors', 'todo'].includes(lowerName)) {
      return true;
    }

    // Check by extension
    const ext = filename.split('.').pop()?.toLowerCase();
    if (!ext || ext === filename.toLowerCase()) {
      // No extension - try to open anyway (might be a script or config)
      return true;
    }

    return TEXT_EXTENSIONS.has(ext);
  }

  onShortcutClick(shortcut: Shortcut): void {
    this.navigateTo(shortcut.path);
  }

  onBreadcrumbClick(index: number): void {
    // Navigate to the path up to this segment
    const path = '/' + this.pathSegments.slice(0, index + 1).join('/');
    this.navigateTo(path);
  }

  goUp(): void {
    if (this.pathSegments.length > 1) {
      const parentPath = '/' + this.pathSegments.slice(0, -1).join('/');
      this.navigateTo(parentPath);
    }
  }

  goHome(): void {
    this.navigateTo('~');
  }

  startPathEdit(): void {
    this.editingPath = true;
    this.editPathValue = this.currentPath;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.pathInput?.nativeElement?.focus();
      this.pathInput?.nativeElement?.select();
    });
  }

  onPathEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const path = this.editPathValue.trim();
      if (path) {
        this.navigateTo(path);
      }
      this.editingPath = false;
    } else if (event.key === 'Escape') {
      this.editingPath = false;
    }
  }

  cancelPathEdit(): void {
    this.editingPath = false;
  }

  refresh(): void {
    this.navigateTo(this.currentPath);
  }

  getFileIcon(item: FileItem): string {
    if (item.type === 'directory') {
      return 'folder';
    }

    // File type icons based on extension
    const ext = item.name.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'png': case 'jpg': case 'jpeg': case 'gif': case 'svg': return 'image';
      case 'mp4': case 'mov': case 'avi': return 'movie';
      case 'mp3': case 'wav': case 'flac': return 'audiotrack';
      case 'zip': case 'tar': case 'gz': case 'rar': return 'archive';
      case 'py': case 'js': case 'ts': case 'java': case 'cpp': case 'c': return 'code';
      case 'json': case 'xml': case 'yaml': case 'yml': return 'data_object';
      case 'md': case 'txt': case 'doc': case 'docx': return 'description';
      case 'csv': case 'xls': case 'xlsx': return 'table_chart';
      default: return 'insert_drive_file';
    }
  }

  formatSize(bytes: number): string {
    if (bytes === 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (bytes >= 1024 && i < units.length - 1) {
      bytes /= 1024;
      i++;
    }
    return `${bytes.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
  }

  formatDate(date: Date | null): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  }

  isHome(): boolean {
    return this.currentPath === this.homePath;
  }

  // ===== Drag and Drop Upload =====

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    // Only intercept file uploads — let dockview tab drags pass through
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.uploadFiles(Array.from(files));
    }
  }

  openFileDialog(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadFiles(Array.from(input.files));
      input.value = ''; // Reset for re-upload of same file
    }
  }

  private uploadFiles(files: File[]): void {
    for (const file of files) {
      const progressItem: { name: string; progress: number; status: string } = { name: file.name, progress: 0, status: 'uploading' };
      this.uploadProgress.push(progressItem);

      const formData = new FormData();
      formData.append('file', file);

      this.http.post(
        `${ConfigService.muxUrl}/commander/upload?path=${encodeURIComponent(this.currentPath)}`,
        formData,
        { reportProgress: true, observe: 'events' }
      ).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            progressItem.progress = Math.round((100 * event.loaded) / event.total);
            this.cdr.detectChanges();
          } else if (event instanceof HttpResponse) {
            progressItem.status = 'done';
            progressItem.progress = 100;
            this.cdr.detectChanges();
            // Remove from progress list after delay
            setTimeout(() => {
              const idx = this.uploadProgress.indexOf(progressItem);
              if (idx > -1) this.uploadProgress.splice(idx, 1);
              this.cdr.detectChanges();
            }, 2000);
            // Refresh directory
            this.refresh();
          }
        },
        error: (err) => {
          console.error('Upload failed:', err);
          progressItem.status = 'error';
          this.cdr.detectChanges();
          setTimeout(() => {
            const idx = this.uploadProgress.indexOf(progressItem);
            if (idx > -1) this.uploadProgress.splice(idx, 1);
            this.cdr.detectChanges();
          }, 3000);
        }
      });
    }
  }

  // ===== Delete =====

  async deleteSelected(): Promise<void> {
    if (!this.selectedItem) return;

    const name = this.selectedItem.name;
    const isDir = this.selectedItem.type === 'directory';
    const message = isDir
      ? `Delete folder "${name}" and all its contents?`
      : `Delete file "${name}"?`;

    if (!await this.dialogs.confirm(message)) return;

    const fullPath = `${this.currentPath}/${name}`;
    this.loading = true;

    this.http.post<any>(`${ConfigService.muxUrl}/commander/delete`, { path: fullPath })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.selectedItem = null;
            this.refresh();
          } else {
            this.error = response.error || 'Delete failed';
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          this.error = err.error?.message || 'Delete failed';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  // ===== Rename =====

  startRename(): void {
    if (!this.selectedItem) return;
    this.renamingItem = this.selectedItem;
    this.renameValue = this.selectedItem.name;
  }

  cancelRename(): void {
    this.renamingItem = null;
    this.renameValue = '';
  }

  confirmRename(): void {
    if (!this.renamingItem || !this.renameValue.trim()) {
      this.cancelRename();
      return;
    }

    const newName = this.renameValue.trim();
    if (newName === this.renamingItem.name) {
      this.cancelRename();
      return;
    }

    const oldPath = `${this.currentPath}/${this.renamingItem.name}`;
    const newPath = `${this.currentPath}/${newName}`;

    this.loading = true;
    this.http.post<any>(`${ConfigService.muxUrl}/commander/rename`, { oldPath, newPath })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.cancelRename();
            this.selectedItem = null;
            this.refresh();
          } else {
            this.error = response.error || 'Rename failed';
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          this.error = err.error?.message || 'Rename failed';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  onRenameKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.confirmRename();
    } else if (event.key === 'Escape') {
      this.cancelRename();
    }
  }

  // ===== Create Folder =====

  async createFolder(): Promise<void> {
    const name = await this.dialogs.prompt('New folder name:');
    if (!name || !name.trim()) return;

    const folderPath = `${this.currentPath}/${name.trim()}`;
    this.loading = true;

    this.http.post<any>(`${ConfigService.muxUrl}/commander/create_directory`, { path: folderPath })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.refresh();
          } else {
            this.error = response.error || 'Failed to create folder';
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          this.error = err.error?.message || 'Failed to create folder';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  // ===== Create File =====

  async createFile(): Promise<void> {
    const name = await this.dialogs.prompt('New file name:');
    if (!name || !name.trim()) return;

    const filePath = `${this.currentPath}/${name.trim()}`;
    this.loading = true;

    // Create empty file
    this.http.post<any>(`${ConfigService.muxUrl}/commander/write_file`, { path: filePath, content: '' })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.refresh();
            // Open in editor
            this.onEditFile.emit({ parentId: this.id, path: filePath });
          } else {
            this.error = response.error || 'Failed to create file';
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          this.error = err.error?.message || 'Failed to create file';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }
}
